import { useState } from 'react';

export default function ContactForm({ preselectedTopic = '' }) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    project_topic: preselectedTopic || 'Mekanisk konstruktion',
    description: '',
    website_url: '', // Honeypot field (strictly required)
  });

  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const topics = [
    'Mekanisk konstruktion',
    'Elkonstruktion & styretavler',
    'Automation & robotter',
    'Automatiske maskiner',
    'Produktionsudstyr',
    'Værktøj & fiksturer',
    'Problemløsning & optimering',
    'Andet / uforpligtende forespørgsel',
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    // Validation
    if (!formData.name.trim() || !formData.email.trim() || !formData.description.trim()) {
      setStatus('error');
      setErrorMessage('Udfyld venligst alle påkrævede felter (Navn, Email og Beskrivelse).');
      return;
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error(`Fejl ved afsendelse: status ${response.status}`);
      }

      setStatus('success');
      setFormData({
        name: '',
        phone: '',
        email: '',
        project_topic: 'Mekanisk konstruktion',
        description: '',
        website_url: '',
      });
    } catch (err) {
      console.error('Submission failed:', err);
      setStatus('error');
      setErrorMessage('Der opstod en fejl under afsendelsen. Prøv venligst igen eller kontakt Jens direkte på +45 22 88 45 36.');
    }
  };

  return (
    <div className="bg-[#0e1622] rounded-xl border border-slate-800 p-6 sm:p-8 md:p-10 shadow-xl relative overflow-hidden">
      {/* Subtle technical corner accents */}
      <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-cyan-500/40 rounded-tl-xl pointer-events-none" />
      <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-cyan-500/40 rounded-tr-xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-cyan-500/40 rounded-bl-xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-cyan-500/40 rounded-br-xl pointer-events-none" />

      <div className="mb-8">
        <span className="text-xs font-open-sans text-cyan-400 uppercase tracking-widest block mb-1">
          Direkte kontakt
        </span>
        <h3 className="font-display text-2xl font-bold text-white tracking-tight">
          Send en besked til JARCon ApS
        </h3>
        <p className="mt-2 text-slate-400 text-sm">
          Udfyld formularen med information om dit projekt. Vi vender hurtigt tilbage.
        </p>
      </div>

      {status === 'success' ? (
        <div className="py-12 px-6 text-center space-y-4 bg-slate-900/80 rounded-lg border border-cyan-500/30">
          <div className="w-16 h-16 bg-cyan-500/10 text-cyan-400 border border-cyan-500/40 rounded-full flex items-center justify-center mx-auto text-2xl">
            <i className="fa-solid fa-check" aria-hidden="true" />
          </div>
          <h4 className="text-xl font-display font-bold text-white">Tak for din henvendelse!</h4>
          <p className="text-slate-300 text-sm max-w-md mx-auto">
            Din besked er sendt til Jens Christensen. Vi gennemgår dit projekt og kontakter dig hurtigst muligt.
          </p>
          <div className="pt-2">
            <button
              type="button"
              onClick={() => setStatus('idle')}
              className="px-5 py-2.5 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm font-medium border border-slate-700 transition-colors"
            >
              Send endnu en besked
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Honeypot field - ABSOLUTELY HIDDEN */}
          <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
            <label htmlFor="website_url">Hjemmeside url (efterlad tom)</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label htmlFor="name" className="block text-xs font-open-sans uppercase tracking-wider text-slate-300 mb-2">
                Navn <span className="text-cyan-400">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder="F.eks. Anders Jensen"
                className="w-full px-4 py-3 bg-[#080d14] border border-slate-700/80 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-colors text-sm"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-xs font-open-sans uppercase tracking-wider text-slate-300 mb-2">
                Telefonnummer <span className="text-cyan-400">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                placeholder="+45 XX XX XX XX"
                className="w-full px-4 py-3 bg-[#080d14] border border-slate-700/80 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-colors text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label htmlFor="email" className="block text-xs font-open-sans uppercase tracking-wider text-slate-300 mb-2">
                Email <span className="text-cyan-400">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="navn@virksomhed.dk"
                className="w-full px-4 py-3 bg-[#080d14] border border-slate-700/80 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-colors text-sm"
              />
            </div>

            <div>
              <label htmlFor="project_topic" className="block text-xs font-open-sans uppercase tracking-wider text-slate-300 mb-2">
                Hvad drejer projektet sig om?
              </label>
              <select
                id="project_topic"
                name="project_topic"
                value={formData.project_topic}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-[#080d14] border border-slate-700/80 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-colors text-sm"
              >
                {topics.map((t) => (
                  <option key={t} value={t} className="bg-slate-900 text-slate-200">
                    {t}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="description" className="block text-xs font-open-sans uppercase tracking-wider text-slate-300 mb-2">
              Beskrivelse af opgaven <span className="text-cyan-400">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              rows={5}
              required
              value={formData.description}
              onChange={handleChange}
              placeholder="Beskriv kort dit projekt, den tekniske udfordring eller maskintype..."
              className="w-full px-4 py-3 bg-[#080d14] border border-slate-700/80 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-colors text-sm leading-relaxed"
            />
          </div>

          {status === 'error' && (
            <div className="p-4 rounded-lg bg-red-950/50 border border-red-800 text-red-300 text-sm flex items-start gap-3">
              <i className="fa-solid fa-triangle-exclamation text-red-400 mt-0.5" aria-hidden="true" />
              <div>{errorMessage || 'Der opstod en fejl. Tjek venligst dine oplysninger.'}</div>
            </div>
          )}

          <button
            type="submit"
            disabled={status === 'submitting'}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white font-bold text-sm tracking-wide transition-all shadow-lg shadow-cyan-950/60 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {status === 'submitting' ? (
              <>
                <i className="fa-solid fa-circle-notch fa-spin text-sm" aria-hidden="true" />
                <span>Sender henvendelse...</span>
              </>
            ) : (
              <>
                <span>Send henvendelse</span>
                <i className="fa-solid fa-paper-plane text-xs" aria-hidden="true" />
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
